import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { createProductOccasion } from '../../services/productServices';

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const emptyCreateForm = {
  label: '',
  slug: '',
  icon_name: '',
  short_description: '',
  featured_on_home: true,
  is_active: true
};

const normalizeId = (value) => Number(value?.id || value);

export default function OccasionSelectModal({
  open,
  occasions = [],
  selected = [],
  onClose,
  onApply,
  onCreated
}) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [localOccasions, setLocalOccasions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLocalOccasions(Array.isArray(occasions) ? occasions : []);
    setSearch('');
    setSelectedIds((selected || []).map(normalizeId).filter(Boolean));
    setShowCreate(false);
    setCreateForm(emptyCreateForm);
    setError('');
  }, [open, selected]);

  const activeOccasions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = Array.isArray(localOccasions) ? localOccasions : [];
    if (!q) return list;
    return list.filter((item) => {
      const haystack = [item.label, item.slug, item.short_description, item.hero_title]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [localOccasions, search]);

  const selectedOccasionObjects = useMemo(() => {
    const ids = new Set(selectedIds.map(Number));
    return (localOccasions || []).filter((item) => ids.has(Number(item.id)));
  }, [localOccasions, selectedIds]);

  const toggleOccasion = (id) => {
    const numericId = Number(id);
    setSelectedIds((prev) => (
      prev.includes(numericId)
        ? prev.filter((item) => item !== numericId)
        : [...prev, numericId]
    ));
  };

  const updateCreateForm = (key, value) => {
    setCreateForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'label' && !prev.slug) next.slug = slugify(value);
      if (key === 'slug') next.slug = slugify(value);
      return next;
    });
  };

  const handleCreateOccasion = async () => {
    const label = createForm.label.trim();
    if (!label) {
      setError('Occasion name is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('label', label);
      formData.append('slug', createForm.slug || slugify(label));
      formData.append('icon_name', createForm.icon_name || '');
      formData.append('short_description', createForm.short_description || '');
      formData.append('hero_title', label);
      formData.append('cta_text', 'Book Your Box');
      formData.append('cta_link', `/checkout?occasion=${encodeURIComponent(label)}`);
      formData.append('featured_on_home', createForm.featured_on_home ? '1' : '0');
      formData.append('is_active', createForm.is_active ? '1' : '0');

      const created = await createProductOccasion(formData);
      if (!created?.id) throw new Error('Occasion was created but response was invalid.');

      setLocalOccasions((prev) => {
        const exists = prev.some((item) => Number(item.id) === Number(created.id));
        return exists ? prev : [...prev, created];
      });
      onCreated?.(created);
      setSelectedIds((prev) => Array.from(new Set([...prev, Number(created.id)])));
      setShowCreate(false);
      setCreateForm(emptyCreateForm);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to create occasion.');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = () => {
    const ids = new Set(selectedIds.map(Number));
    const finalSelection = (localOccasions || []).filter((item) => ids.has(Number(item.id)));
    onApply?.(finalSelection);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Select Occasions
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select all occasions where this product should appear. These selections power the occasion archive pages and product filters.
        </Typography>

        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search occasions..."
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {selectedOccasionObjects.length ? selectedOccasionObjects.map((item) => (
            <Chip
              key={item.id}
              label={item.label}
              onDelete={() => toggleOccasion(item.id)}
              color="warning"
              variant="filled"
            />
          )) : (
            <Typography variant="body2" color="text.secondary">No occasions selected yet.</Typography>
          )}
        </Box>

        <Grid container spacing={1.5}>
          {activeOccasions.length ? activeOccasions.map((item) => {
            const checked = selectedIds.includes(Number(item.id));
            return (
              <Grid item xs={12} sm={6} key={item.id}>
                <Paper
                  variant="outlined"
                  onClick={() => toggleOccasion(item.id)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    borderColor: checked ? '#f97316' : 'divider',
                    bgcolor: checked ? 'rgba(249, 115, 22, 0.08)' : '#fff',
                    transition: '0.18s ease',
                    '&:hover': { borderColor: '#f97316', boxShadow: '0 8px 22px rgba(15,23,42,0.08)' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                    <Checkbox checked={checked} sx={{ p: 0.25, color: '#f97316', '&.Mui-checked': { color: '#f97316' } }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                        {item.icon_name ? `${item.icon_name} ` : ''}{item.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        /{item.slug || slugify(item.label)}
                      </Typography>
                      {item.short_description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {item.short_description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          }) : (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">No occasions found.</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Button
          startIcon={<AddIcon />}
          variant={showCreate ? 'contained' : 'outlined'}
          onClick={() => setShowCreate((value) => !value)}
          sx={{ mb: showCreate ? 2 : 0 }}
        >
          {showCreate ? 'Hide Create Occasion' : 'Create New Occasion'}
        </Button>

        {showCreate && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff8f1' }}>
            <Typography fontWeight={800} sx={{ mb: 1.5 }}>Create New Occasion</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Occasion Name *"
                  value={createForm.label}
                  onChange={(event) => updateCreateForm('label', event.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Wedding, House Party, Baby Shower..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Slug"
                  value={createForm.slug}
                  onChange={(event) => updateCreateForm('slug', event.target.value)}
                  fullWidth
                  size="small"
                  placeholder="auto-generated"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Icon / Emoji"
                  value={createForm.icon_name}
                  onChange={(event) => updateCreateForm('icon_name', event.target.value)}
                  fullWidth
                  size="small"
                  placeholder="🎉"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Short Description"
                  value={createForm.short_description}
                  onChange={(event) => updateCreateForm('short_description', event.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={createForm.featured_on_home} onChange={(event) => updateCreateForm('featured_on_home', event.target.checked)} />}
                  label="Feature on homepage"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={createForm.is_active} onChange={(event) => updateCreateForm('is_active', event.target.checked)} />}
                  label="Active"
                />
              </Grid>
            </Grid>

            {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" onClick={handleCreateOccasion} disabled={saving}>
                {saving ? 'Saving...' : 'Save & Select Occasion'}
              </Button>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply}>
          Save Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
}
